<template>
  <PageShell active="cart">
    <section class="checkout-page">
      <h1>Please fill in the order information</h1>
      <div class="section-title">Shipping Address</div>
      <AddressDialog ref="addressDialog" v-model="addresses" />

      <table class="order-table">
        <thead>
          <tr>
            <th>Part Number</th>
            <th>Product Name</th>
            <th>Qty</th>
            <th>Price, USD</th>
            <th>Sub-total, USD</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in orderItems" :key="index">
            <td>{{ item.part_number }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.num }}</td>
            <td>{{ item.price }}</td>
            <td>{{ item.subtotal }}</td>
          </tr>
          <tr>
            <td>Sub-total</td>
            <td colspan="3"></td>
            <td>{{ payInfo ? payInfo.subtotal : '' }}</td>
          </tr>
        </tbody>
      </table>

      <div class="summary-box">
        <p>Subtotal • {{ orderItems.length }} items: ${{ payInfo ? payInfo.subtotal : '0' }}</p>
        <!-- <p>shipping fee: ${{ payInfo ? payInfo.shipping_fee : '0' }}</p> -->
        <p>Total: ${{ payInfo ? payInfo.subtotal : '0' }}</p>
      </div>
      <button class="submit-btn" type="button" @click="submitOrder">Submit</button>
    </section>
  </PageShell>
</template>

<script>
import PageShell from '@/components/common/PageShell.vue'
import AddressDialog from '@/components/common/AddressDialog.vue'
import { getToken } from '@/util/token-util.js'

export default {
  name: 'CheckoutPage',
  components: {
    PageShell,
    AddressDialog
  },
  data() {
    return {
      addresses: [],
      orderItems: [],
      selectedAddressId: '',
      loading: false,
      payInfo: null,
      itemsJson: ''
    }
  },
  async mounted() {
    if (!getToken()) {
      this.$message.warning('Please log in first')
      this.$router.push({ name: 'home' })
      return
    }
    const itemsStr = this.$route.query.items
    if (!itemsStr) {
      this.$message.warning('No items selected')
      this.$router.push({ name: 'cart' })
      return
    }
    this.itemsJson = itemsStr
    this.loading = true
    try {
      const [addrRes, payRes] = await Promise.all([
        this.$api({ url: 'Address/list', method: 'get' }),
        this.$api({ url: 'Order/getPayInfo', method: 'get', data: { items: itemsStr } })
      ])
      if (addrRes.code === 200) {
        this.addresses = (addrRes.data || []).map(item => ({
          id: item.id,
          name: item.name,
          phone: item.phone,
          address: item.detail_address || '',
        }))
        if (this.addresses.length > 0) {
          this.selectedAddressId = this.addresses[0].id
        }
      }
      if (payRes.code === 200) {
        this.payInfo = payRes.data
        this.orderItems = (payRes.data && payRes.data.items) || []
      }
    } catch (e) {
      this.$message.error('Failed to load checkout data')
    } finally {
      this.loading = false
    }
  },
  methods: {
    async submitOrder() {
      const dialog = this.$refs.addressDialog
      const selectedIndex = dialog ? dialog.selectedIndex : 0
      const selectedAddress = this.addresses[selectedIndex] || this.addresses[0]
      if (!selectedAddress) {
        this.$message.warning('Please add a shipping address')
        return
      }
      this.selectedAddressId = selectedAddress.id
      try {
        const res = await this.$api({
          url: 'Order/submit',
          method: 'post',
          data: {
            address_id: this.selectedAddressId,
            items: this.itemsJson
          }
        })
        if (res.code === 200) {
          this.$message.success('Order submitted successfully')
          // 清除已下单的购物车记录
          try {
            const cartItems = JSON.parse(this.itemsJson)
            await Promise.all(cartItems.map(item =>
              this.$api({ url: 'Cart/remove', method: 'post', data: { cart_item_ids: item.id } })
            ))
            this.$root.$emit('cart-updated')
          } catch (e) {
            // 清除购物车非关键操作，忽略错误
          }
          this.$router.push({ name: 'orders' })
        } else {
          this.$message.error(res.message || 'Failed to submit order')
        }
      } catch (e) {
        this.$message.error('Failed to submit order')
      }
    }
  }
}
</script>

<style scoped>
.checkout-page {
  max-width: 1100px;
  margin: 0 auto;
  min-height: 690px;
  padding: 28px 20px 60px;
}

h1 {
  margin: 0 0 36px;
  font-size: 26px;
  font-weight: 400;
  color: #111;
}

.section-title {
  height: 48px;
  padding: 0 26px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  background: #f5f5f5;
  color: #111;
  font-size: 16px;
}

.empty-address {
  margin: 26px 0 22px;
  color: #111;
  font-size: 16px;
}

.orange-btn,
.submit-btn {
  border: 0;
  border-radius: 4px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
}

.orange-btn {
  width: 146px;
  height: 46px;
}

.order-table {
  width: 100%;
  margin-top: 36px;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  color: #333;
  font-size: 14px;
}

.order-table th,
.order-table td {
  height: 60px;
  border-right: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  text-align: center;
  font-weight: 400;
}

.order-table th {
  background: #f7f7f7;
  color: #111;
}

.order-table th:last-child,
.order-table td:last-child {
  border-right: 0;
}

.order-table tr:last-child td {
  border-bottom: 0;
}

.order-table td:first-child,
.order-table th:first-child {
  width: 160px;
}

.summary-box {
  height: 102px;
  margin-top: 10px;
  padding: 14px 32px;
  background: #fafbfc;
  text-align: right;
  color: #111;
  font-size: 16px;
  line-height: 1.55;
}

.submit-btn {
  width: 140px;
  height: 42px;
  margin: 34px 0;
  float: right;
}
</style>
